/**
 * Storage utility that provides a consistent API for data persistence.
 * Works with both the artifact storage API and falls back to localStorage
 * for standalone deployments.
 */

const storage = {
  /**
   * Get a value from storage
   * @param {string} key - The key to retrieve
   * @returns {Promise<{value: string} | null>}
   */
  async get(key) {
    try {
      // Try artifact storage first (if available)
      if (window.storage?.get && window.storage !== storage) {
        return await window.storage.get(key);
      }
    } catch (e) {
      console.log('Falling back to localStorage for get:', key);
    }
    
    // Fall back to localStorage
    try {
      const value = localStorage.getItem(key);
      return value ? { value } : null;
    } catch (e) {
      console.error('localStorage get error:', e);
      return null;
    }
  },

  /**
   * Set a value in storage
   * @param {string} key - The key to set
   * @param {string} value - The value to store
   * @returns {Promise<{key: string, value: string} | null>}
   */
  async set(key, value) {
    try {
      // Try artifact storage first (if available)
      if (window.storage?.set && window.storage !== storage) {
        return await window.storage.set(key, value);
      }
    } catch (e) {
      console.log('Falling back to localStorage for set:', key);
    }
    
    // Fall back to localStorage
    try {
      localStorage.setItem(key, value);
      return { key, value };
    } catch (e) {
      console.error('localStorage set error:', e);
      return null;
    }
  },

  /**
   * Delete a value from storage
   * @param {string} key - The key to delete
   * @returns {Promise<{key: string, deleted: boolean} | null>}
   */
  async delete(key) {
    try {
      // Try artifact storage first (if available)
      if (window.storage?.delete && window.storage !== storage) {
        return await window.storage.delete(key);
      }
    } catch (e) {
      console.log('Falling back to localStorage for delete:', key);
    }
    
    // Fall back to localStorage
    try {
      localStorage.removeItem(key);
      return { key, deleted: true };
    } catch (e) {
      console.error('localStorage delete error:', e);
      return null;
    }
  },

  /**
   * List all keys with a given prefix
   * @param {string} prefix - The prefix to filter by
   * @returns {Promise<{keys: string[]} | null>}
   */
  async list(prefix = '') {
    try {
      // Try artifact storage first (if available)
      if (window.storage?.list && window.storage !== storage) {
        return await window.storage.list(prefix);
      }
    } catch (e) {
      console.log('Falling back to localStorage for list');
    }
    
    // Fall back to localStorage
    try {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key);
        }
      }
      return { keys };
    } catch (e) {
      console.error('localStorage list error:', e);
      return { keys: [] };
    }
  },

  /**
   * Clear all data (use with caution)
   * @returns {Promise<boolean>}
   */
  async clear() {
    try {
      const result = await this.list('donation-system-');
      if (result?.keys) {
        for (const key of result.keys) {
          await this.delete(key);
        }
      }
      return true;
    } catch (e) {
      console.error('Storage clear error:', e);
      return false;
    }
  }
};

// Make it globally available if not already defined
if (!window.storage) {
  window.storage = storage;
}

export default storage;
